import { useState, useEffect } from 'react';
import { Box } from '@mui/material';
import { useDispatch } from 'react-redux';
import { BaseLayout } from '../../../layouts/base';
import { fontWeight, Heading, headingLevel, iconBtnType, IconButton, Menu, Table } from '../../../packages';
import { ConfirmationModal } from '../../../packages/ui/templates/confirmation-modal/Modal';
import { sxStyles, useStyles } from './Constructs.styles';
import { AddConstructs } from './components';
import { drawerType } from '../../../packages/ui/organisms/drawer';
import { getConstructs, deleteConstruct } from '../../../services/profile/profile.service';
import { showSnackbar } from '../../../redux/modules/snackbar';
import useDebounce from '../../../hooks/useDebounce';

export const Constructs = () => {
  const classes = useStyles();
  const dispatch = useDispatch();

  const [isOpen, setIsOpen] = useState(false);
  const [selectedConstruct, setSelectedConstruct] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [constructs, setConstructs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [tableValue, setTableValue] = useState({
    search: { searchKey: '' },
    pagination: { currentPage: 1, rowsPerPage: 10 },
    sorter: { direction: 'asc', column: 'name' },
  });
  const [totalCount, setTotalCount] = useState(0);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [constructToDelete, setConstructToDelete] = useState(null);

  const renderColumns = [
    {
      id: 'name',
      label: 'Construct',
      textAlign: 'left',
      sortable: true,
      renderCell: value => value || '-',
    },
    {
      id: 'description',
      label: 'Description',
      textAlign: 'left',
      renderCell: value => value || '-',
    },
    {
      id: 'clinicalImportance',
      label: 'Clinical Importance',
      textAlign: 'left',
      renderCell: value => value || '-',
    },
    {
      id: 'updateStrategy',
      label: 'Update Strategy',
      textAlign: 'left',
      renderCell: value => value || '-',
    },
    {
      id: 'trigger',
      label: 'Trigger',
      textAlign: 'left',
      renderCell: value => value || '-',
    },
    {
      id: 'actions',
      label: '',
      textAlign: 'left',
      renderCell: actions => {
        return (
          <div className={classes.actionMenu}>
            <Menu
              icon="more"
              items={[
                {
                  label: 'Delete',
                  onClick: () => {
                    handleDelete(actions.id);
                  },
                },
              ]}
            />
          </div>
        );
      },
    },
  ];

  const debouncedSearchValue = useDebounce(tableValue.search?.searchKey || '', 1000);

  const fetchConstructs = async (searchTerm = '', pageNumber, sorter = null) => {
    try {
      setLoading(true);
      setError(null);

      const sortBy = sorter?.column === 'name' ? 'name' : sorter?.column || 'name';
      const queryParams = {
        pageNumber: pageNumber,
        pageSize: tableValue.pagination.rowsPerPage,
        sortOrder: sorter?.direction,
        sortBy,
      };

      if (searchTerm && searchTerm.trim()) {
        queryParams.search = searchTerm.trim();
      }

      const response = await getConstructs(null, queryParams);

      if (response?.data) {
        const constructs = response.data.constructs || response.data.content || response.data || [];

        const constructsWithActions = constructs.map(construct => ({
          ...construct,
          name: construct.name,
          description: construct.description,
          clinicalImportance: construct.clinicalImportance,
          updateStrategy: construct.updateStrategy,
          trigger: construct.trigger,
          actions: {
            id: construct.id,
          },
        }));

        setConstructs(constructsWithActions);
        setTotalCount(response.data.totalElements || response.data.totalCount || constructs.length);
      }
    } catch (err) {
      setError(err.message || 'Failed to fetch constructs');
      console.error('Error fetching constructs:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchConstructs('', 0);
  }, []);

  useEffect(() => {
    if (debouncedSearchValue !== tableValue.search?.searchKey) {
      setTableValue(prev => ({
        ...prev,
        pagination: {
          ...prev.pagination,
          currentPage: 1,
        },
      }));
    }
  }, [debouncedSearchValue]);

  useEffect(() => {
    const pageNumber = tableValue.pagination.currentPage - 1;
    fetchConstructs(debouncedSearchValue, pageNumber, tableValue.sorter);
  }, [
    tableValue.pagination.currentPage,
    tableValue.pagination.rowsPerPage,
    debouncedSearchValue,
    tableValue.sorter,
  ]);

  const handleOpen = () => {
    setIsEditMode(false);
    setSelectedConstruct(null);
    setIsOpen(true);
  };

  const refreshData = () => {
    const pageNumber = tableValue.pagination.currentPage - 1;
    fetchConstructs(debouncedSearchValue, pageNumber, tableValue.sorter);
  };

  const handleClose = () => {
    setIsOpen(false);
    setSelectedConstruct(null);
    setIsEditMode(false);
  };

  const handleRowClick = rowData => {
    setSelectedConstruct(rowData);
    setIsEditMode(true);
    setIsOpen(true);
  };

  const handleDelete = constructId => {
    const construct = constructs.find(c => c.id === constructId || c._id === constructId);
    setConstructToDelete({ id: constructId, name: construct?.name || 'this construct' });
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = async () => {
    if (!constructToDelete) return;

    try {
      setLoading(true);
      await deleteConstruct({ id: constructToDelete.id });

      dispatch(
        showSnackbar({
          snackType: 'success',
          snackMessage: 'Construct deleted successfully',
          duration: 3000,
        }),
      );

      refreshData();
      setShowDeleteModal(false);
      setConstructToDelete(null);
    } catch (err) {
      setError(err.message || 'Failed to delete construct');
      console.error('Error deleting construct:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCancelDelete = () => {
    setShowDeleteModal(false);
    setConstructToDelete(null);
  };

  const handleTableChange = newValue => {
    setTableValue(newValue);
  };

  return (
    <>
      <AddConstructs
        isOpen={isOpen}
        onClose={handleClose}
        drawerType={drawerType.NORMAL}
        selectedConstruct={selectedConstruct}
        isEditMode={isEditMode}
        onRefresh={refreshData}
      />
      <BaseLayout>
        <Box sx={sxStyles.header}>
          <Box sx={sxStyles.flex}>
            <Heading className={classes.title} level={headingLevel.XL} weight={fontWeight.BOLD}>
              Constructs
            </Heading>
            <Box component="span" sx={sxStyles.totalCount}>
              {totalCount} total
            </Box>
          </Box>
          <Box>
            <IconButton
              className={classes.addConstructBtn}
              icon="plus"
              variant={iconBtnType.PRIMARY}
              onClick={handleOpen}
            >
              Add construct
            </IconButton>
          </Box>
        </Box>
        <Box sx={sxStyles.tableContainer}>
          <Table
            searchProps={{
              placeholder: 'Search constructs by name',
            }}
            gridProps={{
              columns: renderColumns,
              data: constructs,
              isLoading: loading,
              onRowClick: handleRowClick,
              rowStyle: { cursor: 'pointer' },
              emptyState: error ? error : 'No Record Found',
            }}
            paginationProps={{
              currentRows: constructs.length,
              totalCount: totalCount,
              showRowsPerPage: true,
            }}
            value={tableValue}
            onChange={handleTableChange}
          />
        </Box>
      </BaseLayout>

      <ConfirmationModal
        open={showDeleteModal}
        onClose={handleCancelDelete}
        onSave={handleConfirmDelete}
        title="Delete this construct?"
        message={`Are you sure you want to delete "${constructToDelete?.name}"? This action cannot be undone.`}
        noLabel="Cancel"
        yesLabel="Delete"
      />
    </>
  );
};

export default Constructs;
